import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import axios from "axios";
import swal from "sweetalert";
import CheckoutGreenpay from "../payments/CheckoutGreenpay";
import TurnstileWidget from "./TurnstileWidget";
import { PAYMENT_OBJECT } from "../utils/PaymentObject";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(4),
    maxWidth: 640,
    margin: "0 auto",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  section: {
    marginTop: theme.spacing(2),
  },
}));

const LICENSE_TYPES = ["A1", "A2", "A3", "B1", "B2", "B3", "B4", "C1", "C2", "D1", "D2", "D3", "E1", "E2"];

const emptyFormData = {
  is_third_party: false,
  full_name: "",
  document_number: "",
  age: "",
  phone: "",
  email: "",
  license_type: "",
  height_cm: "",
  weight_kg: "",
  is_organ_donor: false,
  handedness: "",
  requires_electronic_invoice: false,
  billing_name: "",
  billing_tax_id: "",
  billing_email: "",
  billing_address: "",
};

export default function DriverMedicalCertificateRequestForm({
  mode,
  price,
  prefill,
  jwt,
  onCompleted,
}) {
  const classes = useStyles();
  const isPrivate = mode === "private";

  const [formData, setFormData] = useState({
    ...emptyFormData,
    ...(prefill || {}),
  });
  const [paymentMethodId, setPaymentMethodId] = useState(1); // 1 = Card/online by default
  const [captchaToken, setCaptchaToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [onlinePaymentFailed, setOnlinePaymentFailed] = useState(false);
  const [useOfflineFallback, setUseOfflineFallback] = useState(false);

  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExp, setCardExp] = useState("");
  const [cardCvv, setCardCvv] = useState("");

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [offlineData, setOfflineData] = useState({
    payment_method_id: "",
    reference: "",
    origin_reference: "",
    comments: "",
    amount: price || "",
  });

  useEffect(() => {
    async function fetchPaymentMethods() {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL_BASE}/paymentMethods`
        );
        setPaymentMethods(response.data || []);
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    }
    fetchPaymentMethods();
  }, []);

  const apiBase = process.env.REACT_APP_API_URL_BASE;
  const createEndpoint = isPrivate
    ? `${apiBase}/driver-medical-certificate`
    : `${apiBase}/public/driver-medical-certificate`;
  const authHeaders = isPrivate ? { Authorization: `Bearer ${jwt}` } : {};

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleThirdPartyToggle = (event) => {
    const checked = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      is_third_party: checked,
      ...(checked
        ? { full_name: "", document_number: "", age: "", phone: "", email: "" }
        : prefill || {}),
    }));
  };

  const handleCreateRequest = async (event) => {
    event.preventDefault();

    if (!isPrivate && !captchaToken) {
      swal("Error", "Por favor completa la verificación anti-bot", "error");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(
        createEndpoint,
        {
          ...formData,
          payment_method_id: paymentMethodId,
          ...(isPrivate ? {} : { captcha_token: captchaToken }),
        },
        { headers: authHeaders }
      );
      setRequest(response.data.certificateRequest || response.data);
    } catch (error) {
      console.error(error);
      swal(
        "Error",
        error.response?.data?.message || "No se pudo crear la solicitud",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOnlinePayment = async () => {
    try {
      setSubmitting(true);
      const PAYMENT_OBJ = {
        paymentData: {
          card: {
            cardHolder: cardName,
            expirationDate: {
              month: parseInt(cardExp.split("-")[1], 10),
              year: parseInt(cardExp.split("-")[0].slice(-2)),
            },
            cardNumber,
            cvc: cardCvv,
            nickname: `card${cardNumber.slice(-4)}`,
          },
        },
        orderRequestData: {
          ...PAYMENT_OBJECT.orderRequestData,
          orderReference: `${request.id}`,
          amount: price,
          description: "PAGO-DE-DICTAMEN-MEDICO",
          additional: {
            ...PAYMENT_OBJECT.orderRequestData.additional,
            customer: {
              ...PAYMENT_OBJECT.orderRequestData.additional.customer,
              name: formData.full_name,
              email: formData.email,
              identification: formData.document_number,
            },
            products: [
              {
                ...PAYMENT_OBJECT.orderRequestData.additional.products[0],
                description: "Dictamen Medico Licencia de Conducir",
                skuId: "SKU-DICTAMEN-MEDICO",
                price,
              },
            ],
          },
        },
      };

      const payEndpoint = isPrivate
        ? `${apiBase}/driver-medical-certificate/${request.id}/pay/online`
        : `${apiBase}/public/driver-medical-certificate/${request.id}/pay/online`;

      await axios.post(payEndpoint, PAYMENT_OBJ, { headers: authHeaders });

      swal(
        "Pago confirmado",
        "Tu pago fue procesado. En las próximas horas recibirás tu dictamen médico en el correo registrado.",
        "success"
      );
      onCompleted && onCompleted();
    } catch (error) {
      console.error(error);
      setOnlinePaymentFailed(true);
      swal(
        "Error",
        error.response?.data?.message ||
          "No se pudo procesar el pago con tarjeta. Puedes intentar de nuevo o reportar un pago manual como alternativa.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOfflinePayment = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      await axios.post(
        `${apiBase}/driver-medical-certificate/${request.id}/pay/offline`,
        { ...offlineData, amount: Number(offlineData.amount) },
        { headers: authHeaders }
      );
      swal(
        "Solicitud registrada",
        "Tu reporte de pago offline fue enviado. Una vez conciliado, procesaremos tu dictamen médico.",
        "success"
      );
      onCompleted && onCompleted();
    } catch (error) {
      console.error(error);
      swal("Error", "No se pudo reportar el pago", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (request) {
    return (
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" gutterBottom>
          Orden #{request.id} - Total: ${price}
        </Typography>

        {paymentMethodId === 1 && !useOfflineFallback ? (
          <div className={classes.section}>
            <Typography variant="subtitle1" gutterBottom>
              Pago en línea
            </Typography>
            <CheckoutGreenpay
              cardName={cardName}
              setCardName={setCardName}
              cardNumber={cardNumber}
              setCardNumber={setCardNumber}
              cardExp={cardExp}
              setCardExp={setCardExp}
              cardCvv={cardCvv}
              setCardCvv={setCardCvv}
              handlePayment={handleOnlinePayment}
              loading={submitting}
            />
            {onlinePaymentFailed && isPrivate && (
              <Alert severity="warning" style={{ marginTop: 16 }}>
                No pudimos procesar tu pago con tarjeta.{" "}
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setUseOfflineFallback(true)}
                >
                  Reportar pago manual en su lugar
                </Button>
              </Alert>
            )}
          </div>
        ) : (
          <form
            onSubmit={handleOfflinePayment}
            className={classes.form + " " + classes.section}
          >
            <Typography variant="subtitle1">Reportar pago offline</Typography>
            <FormControl variant="outlined" fullWidth required>
              <InputLabel>Método de pago</InputLabel>
              <Select
                value={offlineData.payment_method_id}
                onChange={(e) =>
                  setOfflineData((prev) => ({
                    ...prev,
                    payment_method_id: e.target.value,
                  }))
                }
                label="Método de pago"
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Referencia de pago"
              required
              fullWidth
              variant="outlined"
              value={offlineData.reference}
              onChange={(e) =>
                setOfflineData((prev) => ({ ...prev, reference: e.target.value }))
              }
            />
            <TextField
              label="Referencia adicional (opcional)"
              fullWidth
              variant="outlined"
              value={offlineData.origin_reference}
              onChange={(e) =>
                setOfflineData((prev) => ({
                  ...prev,
                  origin_reference: e.target.value,
                }))
              }
            />
            <TextField
              label="Monto"
              type="number"
              required
              fullWidth
              variant="outlined"
              value={offlineData.amount}
              onChange={(e) =>
                setOfflineData((prev) => ({ ...prev, amount: e.target.value }))
              }
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Comentarios"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={offlineData.comments}
              onChange={(e) =>
                setOfflineData((prev) => ({ ...prev, comments: e.target.value }))
              }
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={
                submitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
            >
              {submitting ? "Enviando..." : "Reportar pago"}
            </Button>
          </form>
        )}
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} elevation={2}>
      <Typography variant="h5" gutterBottom>
        Dictamen Médico para Licencia de Conducir
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Precio: ${price}
      </Typography>

      <form onSubmit={handleCreateRequest} className={classes.form}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.is_third_party}
              onChange={handleThirdPartyToggle}
            />
          }
          label="Solicitar para un tercero"
        />

        <TextField
          label="Nombre completo"
          name="full_name"
          required
          fullWidth
          variant="outlined"
          value={formData.full_name}
          onChange={handleChange}
        />
        <TextField
          label="Número de cédula"
          name="document_number"
          required
          fullWidth
          variant="outlined"
          value={formData.document_number}
          onChange={handleChange}
        />
        <TextField
          label="Edad"
          name="age"
          type="number"
          required
          fullWidth
          variant="outlined"
          value={formData.age}
          onChange={handleChange}
        />
        <TextField
          label="Teléfono"
          name="phone"
          required
          fullWidth
          variant="outlined"
          value={formData.phone}
          onChange={handleChange}
        />
        <TextField
          label="Correo electrónico"
          name="email"
          type="email"
          required
          fullWidth
          variant="outlined"
          value={formData.email}
          onChange={handleChange}
        />

        <FormControl variant="outlined" fullWidth required>
          <InputLabel>Tipo de licencia</InputLabel>
          <Select
            name="license_type"
            value={formData.license_type}
            onChange={handleChange}
            label="Tipo de licencia"
          >
            {LICENSE_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Estatura (cm)"
          name="height_cm"
          type="number"
          required
          fullWidth
          variant="outlined"
          value={formData.height_cm}
          onChange={handleChange}
        />
        <TextField
          label="Peso (kg)"
          name="weight_kg"
          type="number"
          required
          fullWidth
          variant="outlined"
          value={formData.weight_kg}
          onChange={handleChange}
        />

        <FormControl component="fieldset">
          <Typography variant="body2">¿Diestro o zurdo?</Typography>
          <RadioGroup
            row
            name="handedness"
            value={formData.handedness}
            onChange={handleChange}
          >
            <FormControlLabel value="right" control={<Radio />} label="Diestro" />
            <FormControlLabel value="left" control={<Radio />} label="Zurdo" />
          </RadioGroup>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              name="is_organ_donor"
              checked={formData.is_organ_donor}
              onChange={handleChange}
            />
          }
          label="¿Eres donador de órganos?"
        />

        <FormControlLabel
          control={
            <Checkbox
              name="requires_electronic_invoice"
              checked={formData.requires_electronic_invoice}
              onChange={handleChange}
            />
          }
          label="Requiero factura electrónica"
        />

        {formData.requires_electronic_invoice && (
          <div className={classes.form}>
            <TextField
              label="Razón social"
              name="billing_name"
              fullWidth
              variant="outlined"
              value={formData.billing_name}
              onChange={handleChange}
            />
            <TextField
              label="Cédula jurídica / fiscal"
              name="billing_tax_id"
              fullWidth
              variant="outlined"
              value={formData.billing_tax_id}
              onChange={handleChange}
            />
            <TextField
              label="Correo de facturación"
              name="billing_email"
              fullWidth
              variant="outlined"
              value={formData.billing_email}
              onChange={handleChange}
            />
            <TextField
              label="Dirección de facturación"
              name="billing_address"
              fullWidth
              variant="outlined"
              value={formData.billing_address}
              onChange={handleChange}
            />
          </div>
        )}

        <FormControl component="fieldset">
          <Typography variant="body2">Método de pago</Typography>
          <RadioGroup
            row
            value={paymentMethodId}
            onChange={(e) => setPaymentMethodId(Number(e.target.value))}
          >
            <FormControlLabel value={1} control={<Radio />} label="Pagar en línea" />
            {isPrivate && (
              <FormControlLabel
                value={3}
                control={<Radio />}
                label="Pago offline / manual"
              />
            )}
          </RadioGroup>
        </FormControl>

        {!isPrivate && (
          <TurnstileWidget onVerify={(token) => setCaptchaToken(token)} />
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          {submitting ? "Enviando..." : "Continuar"}
        </Button>
      </form>
    </Paper>
  );
}
